import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { useSetting } from '@/hooks/useSetting';
import { eventInfo } from '@/mocks/landing-data';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

declare global {
  interface Window {
    Razorpay: new (options: RazorpayOptions) => RazorpayInstance;
  }
}

interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill: {
    name: string;
    email: string;
    contact: string;
  };
  theme: {
    color: string;
  };
  modal?: {
    ondismiss: () => void;
  };
}

interface RazorpayInstance {
  open: () => void;
}

interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

interface TeamData {
  id: string;
  registration_id: string;
  team_name: string;
  status: string;
}

export default function PaymentPage() {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [team, setTeam] = useState<TeamData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [processing, setProcessing] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);
  const [registrationFee, setRegistrationFee] = useState(2000);
  const [convFeeType, setConvFeeType] = useState('percentage');
  const [convFeeValue, setConvFeeValue] = useState(2.5);
  const [gstEnabled, setGstEnabled] = useState(false);
  const [gstPercent, setGstPercent] = useState(18);
  const eventName = useSetting('event_name', eventInfo.name);

  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login');
      return;
    }
    if (!authLoading && user) {
      fetchTeam();
      fetchSettings();
    }
  }, [authLoading, user, navigate]);

  const fetchSettings = async () => {
    try {
      const { data } = await supabase.from('settings').select('key, value').in('key', [
        'registration_fee', 'convenience_fee_type', 'convenience_fee_value',
        'gst_enabled', 'gst_percentage',
      ]);
      if (data) {
        data.forEach((s) => {
          if (s.key === 'registration_fee') setRegistrationFee(parseInt(s.value) || 2000);
          if (s.key === 'convenience_fee_type') setConvFeeType(s.value);
          if (s.key === 'convenience_fee_value') setConvFeeValue(parseFloat(s.value) || 0);
          if (s.key === 'gst_enabled') setGstEnabled(s.value === 'true');
          if (s.key === 'gst_percentage') setGstPercent(parseFloat(s.value) || 18);
        });
      }
    } catch {
      // use defaults
    }
  };

  const fetchTeam = async () => {
    if (!user) return;
    try {
      const { data, error: teamError } = await supabase
        .from('teams')
        .select('id, registration_id, team_name, status')
        .eq('leader_id', user.id)
        .maybeSingle();

      if (teamError) throw teamError;
      if (!data) {
        setError('No team found. Please register first.');
        setLoading(false);
        return;
      }

      setTeam(data);

      if (data.status === 'payment_completed' || data.status === 'verified') {
        setPaymentSuccess(true);
      }

      setLoading(false);
    } catch {
      setError('Failed to load team details.');
      setLoading(false);
    }
  };

  const convenienceFee = convFeeType === 'fixed' ? convFeeValue : Math.round(registrationFee * (convFeeValue / 100));
  const subtotal = registrationFee + convenienceFee;
  const gstAmount = gstEnabled ? Math.round(subtotal * (gstPercent / 100)) : 0;
  const totalAmount = subtotal + gstAmount;

  const handlePayment = async () => {
    if (!team) return;

    setProcessing(true);
    setError('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Please sign in again.');
        setProcessing(false);
        return;
      }

      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/create-razorpay-order`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({ team_id: team.id, amount: totalAmount }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        setError(result.error || 'Order creation failed');
        setProcessing(false);
        return;
      }

      const options: RazorpayOptions = {
        key: result.key_id,
        amount: result.amount,
        currency: result.currency || 'INR',
        name: eventName,
        description: `Registration Fee - ${result.registration_id}`,
        order_id: result.order_id,
        handler: async (response: RazorpayResponse) => {
          await verifyPayment(response);
        },
        prefill: {
          name: team.team_name,
          email: user?.email || '',
          contact: '',
        },
        theme: {
          color: '#0D9488',
        },
        modal: {
          ondismiss: () => {
            setProcessing(false);
          },
        },
      };

      const razorpay = new window.Razorpay(options);
      razorpay.open();
    } catch {
      setError('Something went wrong. Please try again.');
      setProcessing(false);
    }
  };

  const verifyPayment = async (response: RazorpayResponse) => {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        setError('Session expired. Please refresh the page.');
        setProcessing(false);
        return;
      }

      const verifyResponse = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/verify-razorpay-payment`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            team_id: team?.id,
          }),
        }
      );

      const verifyResult = await verifyResponse.json();

      if (!verifyResponse.ok || !verifyResult.verified) {
        setError(verifyResult.error || 'Payment verification failed.');
        setProcessing(false);
        return;
      }

      setPaymentSuccess(true);
      setProcessing(false);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      setError('Payment verification failed. Please contact support.');
      setProcessing(false);
    }
  };

  if (authLoading || loading) {
    return (
      <>
        <Navbar />
        <main className="min-h-screen flex items-center justify-center pt-16 pb-12 bg-background-50">
          <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
        </main>
        <Footer />
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen flex items-center justify-center pt-20 pb-12 bg-background-50">
        <div className="w-full max-w-lg mx-auto px-4">
          {error && !paymentSuccess && (
            <div className="mb-4 p-4 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
              {error.includes('Please register') && (
                <button
                  onClick={() => navigate('/register')}
                  className="block mt-2 text-primary-600 font-medium cursor-pointer hover:underline"
                >
                  Go to Registration →
                </button>
              )}
            </div>
          )}

          {paymentSuccess ? (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-accent-100 flex items-center justify-center mb-6">
                <i className="ri-check-double-line text-3xl text-accent-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Payment Successful!</h1>
              <p className="text-foreground-600 mb-2 text-sm">
                Your registration for <strong>{eventName}</strong> is now confirmed.
              </p>
              {team && (
                <p className="text-foreground-500 mb-6 text-sm">
                  Team ID: <strong className="text-foreground-900">{team.registration_id}</strong>
                </p>
              )}
              <p className="text-foreground-500 mb-8 text-sm">
                You now have access to problem statements and event resources.
              </p>
              <button
                onClick={() => navigate('/dashboard/participant')}
                className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-base transition-colors cursor-pointer whitespace-nowrap"
              >
                Go to Dashboard
              </button>
            </div>
          ) : (
            <div className="bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 text-center">
              <div className="w-16 h-16 mx-auto rounded-full bg-primary-100 flex items-center justify-center mb-6">
                <i className="ri-secure-payment-line text-3xl text-primary-600"></i>
              </div>
              <h1 className="font-heading text-2xl font-bold text-foreground-950 mb-3">Complete Payment</h1>
              <p className="text-foreground-600 mb-8 text-sm max-w-sm mx-auto">
                Pay the registration fee to unlock your dashboard and access the problem statements.
              </p>

              {team && (
                <div className="bg-background-100 rounded-xl p-3 mb-4 text-left">
                  <p className="text-xs text-foreground-500">Team</p>
                  <p className="text-sm font-semibold text-foreground-900">{team.team_name}</p>
                  <p className="text-xs text-foreground-400">{team.registration_id}</p>
                </div>
              )}

              <div className="bg-background-100 rounded-xl p-5 mb-6 text-left">
                <h3 className="font-heading font-semibold text-foreground-800 text-sm mb-3">Payment Summary</h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-foreground-500">Registration Fee</span>
                    <span className="text-foreground-800 font-medium">₹{registrationFee.toLocaleString('en-IN')}.00</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-foreground-500">Convenience Fee{convFeeType === 'percentage' ? ` (${convFeeValue}%)` : ''}</span>
                    <span className="text-foreground-800 font-medium">₹{convenienceFee}.00</span>
                  </div>
                  {gstEnabled && (
                    <div className="flex justify-between">
                      <span className="text-foreground-500">GST ({gstPercent}%)</span>
                      <span className="text-foreground-800 font-medium">₹{gstAmount}.00</span>
                    </div>
                  )}
                  <div className="border-t border-background-200 pt-2 flex justify-between font-semibold">
                    <span className="text-foreground-800">Total Amount</span>
                    <span className="text-foreground-900">₹{totalAmount.toLocaleString('en-IN')}.00</span>
                  </div>
                </div>
              </div>

              <button
                onClick={handlePayment}
                disabled={processing}
                className="w-full py-3 rounded-lg bg-primary-500 hover:bg-primary-600 disabled:bg-primary-400 disabled:cursor-not-allowed text-background-50 dark:text-foreground-950 font-semibold text-base transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
              >
                {processing && <div className="w-4 h-4 rounded-full border-2 border-background-50 border-t-transparent animate-spin"></div>}
                {processing ? 'Processing...' : `Pay ₹${totalAmount.toLocaleString('en-IN')}.00`}
              </button>

              <p className="text-xs text-foreground-400 mt-4">
                Secured by Razorpay. Your payment information is encrypted and secure.
              </p>
            </div>
          )}
        </div>
      </main>
      <Footer />
    </>
  );
}